import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  Modal,
  PageHeader,
  SelectField,
  Spinner,
  TextField,
  useConfirm,
  useToast,
} from '@cartly/ui';
import { FolderPlus, Pencil, RefreshCw, ShoppingBag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { loadSdk } from './EmbeddedSignup';
import { formatRelative } from '../../app/format';
import { useLoader } from '../../app/hooks';
import { useActiveSession, useSession } from '../../app/session';

interface CatalogAccount {
  id: string;
  name: string;
  status: string;
  phoneNumbers: { id: string; displayPhoneNumber: string }[];
  catalog: {
    metaCatalogId: string;
    name: string;
    cartEnabled: boolean;
    catalogVisible: boolean;
    lastSyncAt: string | null;
    lastSyncError: string | null;
    syncedItems: number;
  } | null;
}
interface Category {
  id: string;
  parentId: string | null;
  name: string;
  description: string | null;
  productCount: number;
}
interface CatalogData {
  meta: { appId: string | null; graphVersion: string | null; configId: string | null };
  accounts: CatalogAccount[];
  categories: Category[];
}
interface ProductRow {
  id: string;
  title: string;
  imageUrl: string | null;
}

const MAX_DEPTH = 3;
const CATALOG_SCOPES = 'catalog_management,business_management,whatsapp_business_management';

interface CatalogChoice {
  id: string;
  name: string;
  business: string;
}

/** WhatsApp native shopping: Meta catalog, category > sub-category tree, test sends. */
export function CatalogPage() {
  const session = useActiveSession();
  const { can } = useSession();
  const toast = useToast();
  const confirm = useConfirm();
  const tenantId = session.activeTenant?.id;
  const data = useLoader(() => api.get<CatalogData>('/catalog'), tenantId);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState<{ id?: string; parentId: string | null } | null>(null);
  const [assign, setAssign] = useState<Category | null>(null);
  const [picker, setPicker] = useState<{
    accountId: string;
    loginId: string;
    catalogs: CatalogChoice[];
  } | null>(null);

  if (!data.data) {
    return data.error ? (
      <Alert tone="critical">{data.error}</Alert>
    ) : (
      <Spinner size="lg" label="Loading" />
    );
  }
  const { accounts, categories } = data.data;
  const canManage = can('campaigns:manage');
  const canChannels = can('channels:manage');

  const run = async (key: string, fn: () => Promise<string | void>) => {
    setBusy(key);
    try {
      const message = await fn();
      if (message) toast.success(message);
      await data.reload();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const remove = async (c: Category) => {
    const ok = await confirm({
      title: 'Delete category',
      message: `Delete "${c.name}" and every sub-category inside it? Products stay in your store.`,
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok) await run(`del-${c.id}`, () => api.delete(`/catalog/categories/${c.id}`));
  };

  /** Meta popup: allow catalog access, then choose which catalog to use. */
  const connectWithMeta = async (accountId: string) => {
    const { appId, graphVersion, configId } = data.data!.meta;
    if (!appId || !graphVersion) {
      toast.error('Meta app is not configured on this server');
      return;
    }
    setBusy(`meta-${accountId}`);
    try {
      const fb = await loadSdk(appId, graphVersion);
      const auth = await new Promise<{ code?: string; accessToken?: string } | null>((resolve) =>
        fb.login(
          (r) => {
            const a = r.authResponse;
            resolve(
              a?.code ? { code: a.code } : a?.accessToken ? { accessToken: a.accessToken } : null,
            );
          },
          configId
            ? { response_type: 'code', override_default_response_type: true, config_id: configId }
            : { scope: CATALOG_SCOPES },
        ),
      );
      if (!auth) {
        toast.error('Meta login was cancelled, so nothing changed');
        return;
      }
      const r = await api.post<{ loginId: string; catalogs: CatalogChoice[] }>(
        `/catalog/accounts/${accountId}/meta-login`,
        auth,
      );
      setPicker({ accountId, ...r });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const rows: Array<{ c: Category; depth: number }> = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const c of categories.filter((x) => x.parentId === parentId)) {
      rows.push({ c, depth });
      walk(c.id, depth + 1);
    }
  };
  walk(null, 0);

  const connected = accounts.filter((a) => a.catalog);
  return (
    <div className="stack-lg">
      <PageHeader
        title="WhatsApp shopping"
        description="Customers browse your categories and products inside WhatsApp and send you a cart. They message “shop” to start."
      />
      {accounts.length === 0 && (
        <Alert tone="info" title="Connect WhatsApp first">
          <Link to="/whatsapp">Connect an account</Link> to turn on native shopping.
        </Alert>
      )}

      {accounts.map((a) => (
        <Card
          key={a.id}
          title={a.name}
          subtitle={
            a.catalog ? `Meta catalog ${a.catalog.metaCatalogId}` : 'No catalog connected yet'
          }
          actions={
            a.catalog ? (
              <Button
                icon={<RefreshCw size={16} />}
                loading={busy === `sync-${a.id}`}
                disabled={!canChannels}
                onClick={() =>
                  run(`sync-${a.id}`, async () => {
                    const r = await api.post<{ sent: number; skipped: number; removed: number }>(
                      `/catalog/accounts/${a.id}/sync`,
                    );
                    return `Synced ${r.sent} items${r.skipped ? `, ${r.skipped} skipped (no image)` : ''}`;
                  })
                }
              >
                Sync products to Meta
              </Button>
            ) : (
              <div className="row">
                <Button
                  variant="primary"
                  icon={<ShoppingBag size={16} />}
                  loading={busy === `meta-${a.id}`}
                  disabled={!canChannels}
                  onClick={() => void connectWithMeta(a.id)}
                >
                  Connect with Meta
                </Button>
                <Button
                  loading={busy === `connect-${a.id}`}
                  disabled={!canChannels}
                  onClick={() =>
                    run(`connect-${a.id}`, async () => {
                      await api.post(`/catalog/accounts/${a.id}/connect`);
                      return 'Catalog connected';
                    })
                  }
                >
                  Create automatically
                </Button>
              </div>
            )
          }
        >
          {a.catalog ? (
            <div className="stack">
              <div className="row">
                <Badge
                  tone={
                    a.catalog.lastSyncError
                      ? 'critical'
                      : a.catalog.lastSyncAt
                        ? 'success'
                        : 'neutral'
                  }
                >
                  {a.catalog.lastSyncAt
                    ? `${a.catalog.syncedItems} items · synced ${formatRelative(a.catalog.lastSyncAt)}`
                    : 'Not synced yet'}
                </Badge>
              </div>
              {a.catalog.lastSyncError && <Alert tone="critical">{a.catalog.lastSyncError}</Alert>}
              <Checkbox
                label="Show the cart button"
                checked={a.catalog.cartEnabled}
                disabled={!canChannels || busy === `set-${a.id}`}
                onChange={(e) =>
                  run(`set-${a.id}`, () =>
                    api.patch(`/catalog/accounts/${a.id}/settings`, {
                      cartEnabled: e.target.checked,
                      catalogVisible: a.catalog!.catalogVisible,
                    }),
                  )
                }
              />
              <Checkbox
                label="Show the catalog icon in chats"
                checked={a.catalog.catalogVisible}
                disabled={!canChannels || busy === `set-${a.id}`}
                onChange={(e) =>
                  run(`set-${a.id}`, () =>
                    api.patch(`/catalog/accounts/${a.id}/settings`, {
                      cartEnabled: a.catalog!.cartEnabled,
                      catalogVisible: e.target.checked,
                    }),
                  )
                }
              />
            </div>
          ) : (
            <p className="text-muted">
              “Connect with Meta” opens Meta’s login: allow catalog access, then pick the catalog to
              use. “Create automatically” only works if your access token already has the
              catalog_management permission.
            </p>
          )}
        </Card>
      ))}

      <Card
        title="Categories"
        subtitle="Up to 3 levels, for example Men › Shirts › Linen. Names show as WhatsApp list titles (24 characters)."
        actions={
          canManage && (
            <Button icon={<FolderPlus size={16} />} onClick={() => setForm({ parentId: null })}>
              New category
            </Button>
          )
        }
      >
        {rows.length === 0 ? (
          <EmptyState title="No categories yet">
            Without categories, “shop” sends your whole catalog. Add categories to let customers
            browse step by step.
          </EmptyState>
        ) : (
          <div className="stack">
            {rows.map(({ c, depth }) => (
              <div
                key={c.id}
                className={`tree-row${depth > 0 ? ` tree-row--d${Math.min(depth, 2)}` : ''}`}
              >
                <div>
                  <strong>{c.name}</strong>{' '}
                  <span className="text-muted text-sm">
                    {c.productCount} product{c.productCount === 1 ? '' : 's'}
                    {c.description ? ` · ${c.description}` : ''}
                  </span>
                </div>
                {canManage && (
                  <div className="row">
                    <Button size="sm" onClick={() => setAssign(c)}>
                      Products
                    </Button>
                    {depth + 1 < MAX_DEPTH && (
                      <Button size="sm" onClick={() => setForm({ parentId: c.id })}>
                        Add sub-category
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      aria-label={`Rename ${c.name}`}
                      icon={<Pencil size={16} />}
                      onClick={() => setForm({ id: c.id, parentId: c.parentId })}
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      iconOnly
                      aria-label={`Delete ${c.name}`}
                      icon={<Trash2 size={16} />}
                      loading={busy === `del-${c.id}`}
                      onClick={() => void remove(c)}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {connected.length > 0 && <TestSend accounts={connected} categories={categories} />}

      {picker && (
        <CatalogPicker
          picker={picker}
          onClose={() => setPicker(null)}
          onDone={() => {
            setPicker(null);
            void data.reload();
          }}
        />
      )}
      {form && (
        <CategoryModal
          existing={form.id ? categories.find((c) => c.id === form.id) : undefined}
          parentId={form.parentId}
          onClose={() => setForm(null)}
          onSaved={() => {
            setForm(null);
            void data.reload();
          }}
        />
      )}
      {assign && (
        <ProductsModal
          category={assign}
          onClose={() => setAssign(null)}
          onSaved={() => {
            setAssign(null);
            void data.reload();
          }}
        />
      )}
    </div>
  );
}

function CategoryModal({
  existing,
  parentId,
  onClose,
  onSaved,
}: {
  existing: Category | undefined;
  parentId: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(existing?.name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = { name, description };
      if (existing) await api.patch(`/catalog/categories/${existing.id}`, body);
      else await api.post('/catalog/categories', { ...body, parentId });
      onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      title={existing ? 'Rename category' : parentId ? 'New sub-category' : 'New category'}
      onClose={onClose}
      footer={
        <Button
          variant="primary"
          loading={saving}
          disabled={!name.trim()}
          onClick={() => void save()}
        >
          Save
        </Button>
      }
    >
      <div className="stack">
        <TextField
          label="Name"
          value={name}
          maxLength={24}
          hint={`${name.length}/24 · shown as the WhatsApp list title`}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label="Description (optional)"
          value={description}
          maxLength={72}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>
    </Modal>
  );
}

function ProductsModal({
  category,
  onClose,
  onSaved,
}: {
  category: Category;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const current = useLoader(
    () =>
      api
        .get<{ items: ProductRow[] }>(`/catalog/categories/${category.id}/products`)
        .then((r) => r.items),
    category.id,
  );
  const results = useLoader(
    () =>
      api
        .get<{ items: ProductRow[] }>(
          `/catalog/products?search=${encodeURIComponent(search)}&limit=50`,
        )
        .then((r) => r.items),
    search,
  );
  const [picked, setPicked] = useState<Map<string, ProductRow> | null>(null);
  const [saving, setSaving] = useState(false);

  const selected = picked ?? (current.data ? new Map(current.data.map((p) => [p.id, p])) : null);
  const toggle = (p: ProductRow) => {
    if (!selected) return;
    const next = new Map(selected);
    if (next.has(p.id)) next.delete(p.id);
    else next.set(p.id, p);
    setPicked(next);
  };
  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await api.put(`/catalog/categories/${category.id}/products`, {
        productIds: [...selected.keys()],
      });
      onSaved();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  // Selected products stay visible even when the search no longer lists them.
  const listed = new Map((results.data ?? []).map((p) => [p.id, p]));
  for (const p of selected?.values() ?? []) if (!listed.has(p.id)) listed.set(p.id, p);

  return (
    <Modal
      open
      title={`Products in ${category.name}`}
      onClose={onClose}
      footer={
        <Button variant="primary" loading={saving} disabled={!selected} onClick={() => void save()}>
          Save {selected ? `(${selected.size})` : ''}
        </Button>
      }
    >
      <div className="stack">
        <TextField
          label="Search products"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {!selected || !results.data ? (
          <Spinner label="Loading" />
        ) : listed.size === 0 ? (
          <p className="text-muted">No products found. Products appear here after a store sync.</p>
        ) : (
          <div className="stack scroll-list">
            {[...listed.values()].map((p) => (
              <Checkbox
                key={p.id}
                label={p.title}
                checked={selected.has(p.id)}
                onChange={() => toggle(p)}
              />
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
}

function TestSend({
  accounts,
  categories,
}: {
  accounts: CatalogAccount[];
  categories: Category[];
}) {
  const toast = useToast();
  const phones = accounts.flatMap((a) => a.phoneNumbers);
  const [phoneNumberId, setPhoneNumberId] = useState(phones[0]?.id ?? '');
  const [to, setTo] = useState('');
  const [kind, setKind] = useState<'menu' | 'catalog' | 'category'>('menu');
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? '');
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await api.post('/catalog/send', {
        phoneNumberId,
        to,
        kind,
        ...(kind === 'category' ? { categoryId } : {}),
      });
      toast.success('Sent');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSending(false);
    }
  };
  return (
    <Card
      title="Try it"
      subtitle="Send a shopping message to a number. It must have messaged you in the last 24 hours."
    >
      <div className="stack">
        <SelectField
          label="From"
          value={phoneNumberId}
          onChange={(e) => setPhoneNumberId(e.target.value)}
          options={phones.map((p) => ({ value: p.id, label: p.displayPhoneNumber }))}
        />
        <TextField
          label="To (with country code)"
          value={to}
          placeholder="919876543210"
          onChange={(e) => setTo(e.target.value)}
        />
        <SelectField
          label="Send"
          value={kind}
          onChange={(e) => setKind(e.target.value as typeof kind)}
          options={[
            { value: 'menu', label: 'Category menu' },
            { value: 'catalog', label: 'Whole catalog' },
            ...(categories.length ? [{ value: 'category', label: 'One category' }] : []),
          ]}
        />
        {kind === 'category' && (
          <SelectField
            label="Category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
          />
        )}
        <div>
          <Button
            variant="primary"
            loading={sending}
            disabled={!phoneNumberId || to.trim().length < 8}
            onClick={() => void send()}
          >
            Send
          </Button>
        </div>
      </div>
    </Card>
  );
}

function CatalogPicker({
  picker,
  onClose,
  onDone,
}: {
  picker: { accountId: string; loginId: string; catalogs: CatalogChoice[] };
  onClose: () => void;
  onDone: () => void;
}) {
  const toast = useToast();
  const [chosen, setChosen] = useState(picker.catalogs[0]?.id ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/catalog/accounts/${picker.accountId}/meta-login/select`, {
        loginId: picker.loginId,
        catalogId: chosen,
      });
      toast.success('Catalog connected');
      onDone();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal
      open
      title="Choose a catalog"
      onClose={onClose}
      footer={
        <Button variant="primary" loading={saving} disabled={!chosen} onClick={() => void save()}>
          Use this catalog
        </Button>
      }
    >
      {picker.catalogs.length === 0 ? (
        <Alert tone="info">
          No catalogs found in your Meta business. Create one in Commerce Manager, or close this and
          choose “Create automatically”.
        </Alert>
      ) : (
        <div className="stack scroll-list">
          {picker.catalogs.map((c) => (
            <label key={c.id} className="checkbox">
              <input
                type="radio"
                name="catalog"
                checked={chosen === c.id}
                onChange={() => setChosen(c.id)}
              />
              <span>
                {c.name}
                <span className="field__hint">
                  {' '}
                  — {c.business ? `${c.business} · ` : ''}
                  {c.id}
                </span>
              </span>
            </label>
          ))}
        </div>
      )}
    </Modal>
  );
}
